// services/authService.ts
import { User, Role } from '../types';
import { clearProductCache } from './productos';

const API_BASE_URL: string = process.env.REACT_APP_API_URL || '';

export interface AuthResponse {
  user: User;
  message?: string;
}

const authService = (() => {
  // Solo guardamos datos del usuario (no el token)
  const getUser = (): User | null => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  };

  const setUser = (user: User | null): void => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  };

  const login = async (email: string, password: string): Promise<AuthResponse> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Importante: incluir cookies
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error en el login');
      }

      const data: AuthResponse = await response.json();

      // Solo guardar datos del usuario (el token está en la cookie)
      setUser(data.user);

      // Limpiamos la caché de productos al iniciar sesión para asegurar que el usuario vea datos actualizados
      // y no datos de una sesión anterior o anónima si aplicara.
      clearProductCache();

      return data;
    } catch (error) {
      console.error('Error en login:', error);
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Error en logout:', error);
    } finally {
      setUser(null);
      // Limpiar caché de productos al salir para evitar que persistan datos privados en el navegador
      clearProductCache();
    }
  };

  const isAuthenticated = (): boolean => {
    return !!getUser();
  };

  const getUserRoles = (): Role[] => {
    const user = getUser();
    const roles = user?.roles;
    return Array.isArray(roles) ? roles : [];
  };

  const hasRole = (role: string): boolean => {
    const roles = getUserRoles();
    const [uniqueRol] = roles ?? [];
    return uniqueRol?.name === role;
  };

  const isAdmin = (): boolean => hasRole('admin');
  const isManager = (): boolean => hasRole('manager');
  const isEmployee = (): boolean => hasRole('employee');

  // Fetch autenticado (la cookie se envía automáticamente)
  const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
    const config: RequestInit = {
      ...options,
      credentials: 'include', // Siempre incluir cookies
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    };

    const response = await fetch(url, config);

    return response;
  };

  const verifyToken = async (): Promise<boolean> => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/auth/verify`);

      if (response.ok) {
        const data = await response.json()
        setUser(data.user); // Actualizar datos del usuario
        return true;
      }
      return false;
    } catch (error) {
      setUser(null);
      return false;
    }
  };

  return {
    login,
    logout,
    isAuthenticated,
    getUser,
    getUserRoles,
    hasRole,
    isAdmin,
    isManager,
    isEmployee,
    authenticatedFetch,
    verifyToken
  };
})();

export default authService;